import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Background } from '@app/shared/models/background';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Customization } from '../../shared/models/customization';
import { Logo } from '../../shared/models/logo';

// Sentinel marking a SINGLE create call's own failure inside a per-item settled batch (see
// addTeamBackgrounds/addTeamLogos below) -- a shape no real background/logo API response could
// ever produce, so isCreateItemFailure can tell the two apart without inspecting HTTP status
// codes. Introduced for D5 (docs/ultra-review/2026-09-16-sso-and-card-error-investigation-delta/
// REPORT.md): a shared forkJoin over the raw per-URL requests used to let ONE failing URL in a
// 2+-item batch reject the whole call, discarding a sibling URL's already-succeeded result --
// which a caller retrying afterwards would then resend and duplicate server-side.
export interface CreateItemFailure {
  failedToCreate: true;
  // `any`: the rejection reason is whatever HttpClient's raw HttpErrorResponse-shaped rejection
  // happens to be for this call -- extractHttpErrorMessage/aggregateReasons (the only consumers,
  // in customization.effects.ts) already type their own input as `any` for the same reason.
  reason: any;
}

// `any`: this narrows an item pulled out of a per-item settled array whose success branch can be
// any create-endpoint response shape (Background/Logo-adjacent, never `any` at its own call
// sites) -- the type guard's whole job is to distinguish the ONE known failure shape from
// whatever that success shape is, so the parameter itself can't be narrower than `any` up front.
export function isCreateItemFailure(item: any): item is CreateItemFailure {
  return !!item && item.failedToCreate === true;
}

// Sentinel marking a SINGLE delete call's own failure inside a per-item settled batch (see
// deleteTeamBackgrounds/deleteTeamLogos below) -- mirrors CreateItemFailure/isCreateItemFailure
// above, on the delete side, for F4 (ultra-review finding, user-confirmed 2026-09-22): a shared
// forkJoin over the raw per-id DELETE requests used to let ONE failing id in a 2+-item batch
// reject the whole call, discarding a sibling id's already-succeeded deletion -- which the reducer
// then kept queued for a retry that resent a DELETE for something already gone server-side.
export interface DeleteItemFailure {
  failedToDelete: true;
  // `any`: same reasoning as CreateItemFailure#reason above -- the rejection reason is whatever
  // HttpClient's raw HttpErrorResponse-shaped rejection happens to be for this call.
  reason: any;
}

// `any`: same reasoning as isCreateItemFailure above -- the success branch of a settled delete
// call is the raw `{}` response HttpClient's delete<{}>() resolves to, never `any` at its own call
// sites, so the type guard's parameter can't be narrower than `any` up front.
export function isDeleteItemFailure(item: any): item is DeleteItemFailure {
  return !!item && item.failedToDelete === true;
}

@Injectable({
  providedIn: 'root'
})
export class CustomizationService {

  constructor(private http: HttpClient) { }

  headers = new HttpHeaders().set('Authorization', 'true');

  // Team
  getTeamCustomization(team: string, customizationId: string): Observable<{ customization: Customization }> {
    return this.http.get<{ customization: Customization }>('service:customization' + '/team/' + team + '/customization/' + customizationId, { headers: this.headers });
  }

  listTeamCustomizations(team: string): Observable<{ customizations: Customization[] }> {
    return this.http.get<{ customizations: Customization[] }>('service:customization' + '/team/' + team + '/customizations', { headers: this.headers });
  }

  createTeamCustomization(team: string, name?: string, description?: string): Observable<{ logo: Logo }> {
    return this.http.post<{ logo: Logo }>('service:customization' + '/team/' + team + '/customization', { name, description }, { headers: this.headers });
  }

  listTeamBackgrounds(team: string): Observable<{ backgrounds: Background[] }> {
    return this.http.get<{ backgrounds: Background[] }>('service:customization' + '/team/' + team + '/backgrounds', { headers: this.headers });
  }

  listTeamLogos(team: string): Observable<{ logos: Logo[] }> {
    return this.http.get<{ logos: Logo[] }>('service:customization' + '/team/' + team + '/logos', { headers: this.headers });
  }

  addTeamBackground(url: string, team: string): Observable<Background> {
    return this.http.post<Background>('service:customization' + '/team/' + team + '/background', { background: url }, { headers: this.headers });
  }

  addTeamLogo(url: string, team: string): Observable<{ logo: Logo }> {
    return this.http.post<{ logo: Logo }>('service:customization' + '/team/' + team + '/logo', { logo: url }, { headers: this.headers });
  }

  deleteTeamBackground(id: string, team: string): Observable<{}> {
    return this.http.delete<{}>('service:customization' + '/team/' + team + '/background/' + id, { headers: this.headers });
  }

  deleteTeamLogo(id: string, team: string): Observable<{}> {
    return this.http.delete<{}>('service:customization' + '/team/' + team + '/logo/' + id, { headers: this.headers });
  }

  // Each URL's create call settles independently (map to the raw success value, catchError to a
  // CreateItemFailure sentinel) so this forkJoin never rejects as a WHOLE batch -- a rejection
  // here would discard every sibling URL's already-succeeded result along with the one that
  // failed (D5, docs/ultra-review/2026-09-16-sso-and-card-error-investigation-delta/REPORT.md).
  // withRealCreatedIds/withRealCreatedId (customization.effects.ts) tell a real result from a
  // failure via isCreateItemFailure and only retry the items that actually failed.
  addTeamBackgrounds(urls: string[], team: string): Observable<any> {
    const reqs = [];
    if (urls && urls.length) {
      urls.forEach(url => {
        reqs.push(this.addTeamBackground(url, team).pipe(
          // `as CreateItemFailure`: the object literal already structurally matches the
          // interface (`failedToCreate: true` is a literal type, not `boolean`) -- the cast only
          // documents intent at the call site, TypeScript would infer the same shape unaided.
          catchError(reason => of({ failedToCreate: true, reason } as CreateItemFailure))
        ));
      });
      return forkJoin(reqs);
    } else { return of([]) };
  }

  // Same reasoning as addTeamBackgrounds above.
  addTeamLogos(urls: string[], team: string): Observable<any> {
    const reqs = [];
    if (urls && urls.length) {
      urls.forEach(url => {
        reqs.push(this.addTeamLogo(url, team).pipe(
          // `as CreateItemFailure`: same reasoning as addTeamBackgrounds above.
          catchError(reason => of({ failedToCreate: true, reason } as CreateItemFailure))
        ));
      });
      return forkJoin(reqs);
    } else { return of([]) };
  }

  // Each id's delete call settles independently (catchError to a DeleteItemFailure sentinel) so
  // this forkJoin never rejects as a WHOLE batch -- a rejection here would discard every sibling
  // id's already-succeeded deletion along with the one that failed (F4, ultra-review finding,
  // user-confirmed 2026-09-22, mirroring addTeamBackgrounds/addTeamLogos above). withRemainingToDelete
  // (customization.effects.ts) tells a real result from a failure via isDeleteItemFailure and only
  // keeps the items that actually failed queued for retry.
  deleteTeamBackgrounds(ids: string[], team: string): Observable<any> {
    const reqs = [];
    if (ids && ids.length) {
      ids.forEach(id => {
        reqs.push(this.deleteTeamBackground(id, team).pipe(
          // `as DeleteItemFailure`: the object literal already structurally matches the interface
          // (`failedToDelete: true` is a literal type, not `boolean`) -- the cast only documents
          // intent at the call site, TypeScript would infer the same shape unaided.
          catchError(reason => of({ failedToDelete: true, reason } as DeleteItemFailure))
        ));
      });
      return forkJoin(reqs);
    } else { return of([]) };
  }

  // Same reasoning as deleteTeamBackgrounds above.
  deleteTeamLogos(ids: string[], team: string): Observable<any> {
    const reqs = [];
    if (ids && ids.length) {
      ids.forEach(id => {
        reqs.push(this.deleteTeamLogo(id, team).pipe(
          // `as DeleteItemFailure`: same reasoning as deleteTeamBackgrounds above.
          catchError(reason => of({ failedToDelete: true, reason } as DeleteItemFailure))
        ));
      });
      return forkJoin(reqs);
    } else { return of([]) };
  }

  attachTeamCustomizationBackground(teamId: string, customizationId: string, backgroundId: string): Observable<{}> {
    return this.http.put<{}>('service:customization' + '/team/' + teamId + '/customization/' + customizationId + '/background/' + backgroundId + '/attach', {}, { headers: this.headers });
  }

  attachTeamCustomizationLogo(teamId: string, customizationId: string, logoId: string): Observable<{}> {
    return this.http.put<{}>('service:customization' + '/team/' + teamId + '/customization/' + customizationId + '/logo/' + logoId + '/attach', {}, { headers: this.headers });
  }

  getManagedCustomization(id: string): Observable<{ customization: Customization }> {
    return this.http.get<{ customization: Customization }>('service:customization' + '/managedCustomization/' + id, { headers: this.headers });
  }

  listManagedBackgrounds(): Observable<{ backgrounds: Background[] }> {
    return this.http.get<{ backgrounds: Background[] }>('service:customization' + '/managedBackgrounds', { headers: this.headers });
  }

  listManagedLogos(): Observable<{ logos: Logo[] }> {
    return this.http.get<{ logos: Logo[] }>('service:customization' + '/managedLogos', { headers: this.headers });
  }

  listManagedCustomizations(): Observable<{ customizations: Customization[] }> {
    return this.http.get<{ customizations: Customization[] }>('service:customization' + '/managedCustomizations', { headers: this.headers });
  }

}
